/**
 * How the result strip reads a failed SSH leg, from its STRUCTURED code (`SshTunnelError.code`, main's
 * `sshTunnelErrors.ts`) — never from the sentence (C2 review C2-01):
 * - an OUTAGE or a sign-in problem (unreachable bastion, refused key or password, a key file that is missing …) keeps
 *   Save anyway, like any other failed test — the settings may be right and the server down;
 * - a VALIDATION refusal (the tunnel cannot forward this target or port) and a SECURITY refusal (the bastion's host key
 *   changed or cannot be recorded, a saved secret bound to another bastion) withhold it: saving would only store what
 *   the app refuses, and a changed host key is a security warning to act on, not an outage to wait out. The reason is
 *   shown instead, and the save handler refuses too (`useConnectionSave`).
 */
import type { TestStepwiseResult } from '@/types/electron';

type SshError = NonNullable<NonNullable<TestStepwiseResult['ssh']>['error']>;

export type SshStripFailure = {
  /** The notice's title and the short tier's lead. */
  title: string;
  /** The SSH chip's reading. */
  chip: 'Failed' | 'Refused' | 'Not valid';
  /** The field the failure is about. */
  field: string;
  /** Save anyway's wording (`network` / `auth` / `other`), or why it is withheld. */
  saveAnyway: { kind: 'ssh-network' | 'ssh-auth' | 'network' | 'other' } | { withheld: string };
};

const CHECK_AGAIN = 'then test again.';

export function sshStripFailure(error: SshError | undefined): SshStripFailure {
  switch (error?.code) {
    case 'SSH_HOST_KEY_CHANGED':
      return {
        title: 'SSH host key changed', chip: 'Refused', field: '#ssh-host',
        saveAnyway: { withheld: `Save anyway is not offered for a changed host key: confirm the new key with the server's administrator, use Forget saved host key under SSH host, ${CHECK_AGAIN}` },
      };
    case 'SSH_HOST_KEY_PERSIST_FAILED':
      return {
        title: 'SSH host key not recorded', chip: 'Refused', field: '#ssh-host',
        saveAnyway: { withheld: `Save anyway is not offered while the bastion's host key cannot be recorded: fix ~/.ssh/jam_known_hosts, ${CHECK_AGAIN}` },
      };
    case 'SSH_SECRET_IDENTITY_MISMATCH':
      return {
        title: 'Saved SSH secret belongs to another bastion', chip: 'Refused', field: error.authKind === 'key' ? '#ssh-passphrase' : '#ssh-password',
        saveAnyway: { withheld: `Save anyway is not offered: enter the SSH secret again for this bastion, ${CHECK_AGAIN}` },
      };
    case 'SSH_TUNNEL_BAD_TARGET':
      return {
        title: 'SSH tunnel target not supported', chip: 'Not valid', field: error.reason === 'auto-port' ? '#port' : error.reason === 'oracle-non-ezconnect' ? '#oracleConnectionStringType' : '#server',
        saveAnyway: { withheld: `Save anyway is not offered for a target the SSH tunnel cannot forward: correct it, ${CHECK_AGAIN}` },
      };
    case 'SSH_BAD_PORT':
      return {
        title: 'SSH port not valid', chip: 'Not valid', field: '#ssh-port',
        saveAnyway: { withheld: `Save anyway is not offered for an SSH port the tunnel cannot use: correct it, ${CHECK_AGAIN}` },
      };
    case 'SSH_AUTH_FAILED':
    case 'SSH_AGENT_UNAVAILABLE':
    case 'SSH_SECRET_UNAVAILABLE':
      return { title: 'SSH tunnel failed', chip: 'Failed', field: '#ssh-user', saveAnyway: { kind: 'ssh-auth' } };
    case 'SSH_KEY_NOT_FOUND':
    case 'SSH_KEY_UNREADABLE':
    case 'SSH_KEY_BAD_PASSPHRASE':
      return { title: 'SSH tunnel failed', chip: 'Failed', field: '#ssh-keypath', saveAnyway: { kind: 'ssh-auth' } };
    case 'SSH_FORWARD_FAILED':
      return { title: 'SSH tunnel failed', chip: 'Failed', field: '#server', saveAnyway: { kind: 'network' } };
    case 'SSH_HOST_UNREACHABLE':
      return { title: 'SSH tunnel failed', chip: 'Failed', field: '#ssh-host', saveAnyway: { kind: 'ssh-network' } };
    default:
      return { title: 'SSH tunnel failed', chip: 'Failed', field: '#ssh-host', saveAnyway: { kind: 'other' } };
  }
}
